/** GxSegmented: one choice from a short labelled scale (a radio group drawn as joined segments). Style lives in theme.css. */
import type { CSSProperties } from "react";
import { RovingGroup } from "./roving";
export interface SegmentOption<T extends string | number> { readonly value: T; readonly label: string }

/** Columns of a choice control: up to 7 options share one row; more wrap into rows of equal length (8 as 4+4, 9 as 3+3+3, 10 as 5+5), never an empty cell (a count with no even split, such as 11, ends on a short row). */
export const segmentColumnCount = (n: number): number => {
  if (n <= 7) return Math.max(1, n);
  for (const cols of [5, 4, 3, 6, 7]) if (n % cols === 0) return cols;
  return Math.ceil(n / Math.ceil(n / 5)); // no even split
};

export function GxSegmented<T extends string | number>({ label, options, value, onChange, testId }: {
  readonly label: string; readonly options: readonly SegmentOption<T>[]; readonly value: T | null; readonly onChange: (value: T) => void; readonly testId?: string;
}) {
  return (
    <RovingGroup className="gx-segmented" role="radiogroup" aria-label={label} data-testid={testId} style={{ gridTemplateColumns: `repeat(${segmentColumnCount(options.length)}, minmax(0, 1fr))` } as CSSProperties}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} className="gx-segment" data-testid={testId === undefined ? undefined : `${testId}.${o.value}`} onClick={() => onChange(o.value)}>
          <b className="gx-num">{o.value}</b>
          <span>{o.label}</span>
        </button>
      ))}
    </RovingGroup>
  );
}
